// Cloudflare's Turnstile dashboard numbers on the turnstile integration page:
// how many security checks were shown, how many were passed, and where they
// came from. Up to a week is read live from Cloudflare; longer windows come
// from the days ff-api stores, since Cloudflare will not answer further back.
import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useTurnstileAnalyticsQuery } from '@/client/api/integrations/integrations.query';
import { TurnstileAnalytics as Analytics, TurnstileTopRow } from '@/client/types/integrations';
import { fmtNum, fmtPct } from './integrationsUi';

const Stat: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="min-w-0">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p className="text-xl font-semibold text-gray-900">{value}</p>
    {hint && <p className="text-xs text-gray-400">{hint}</p>}
  </div>
);

const fmtBucket = (t: string, bucket?: string) =>
  new Date(t).toLocaleString(undefined, bucket === 'day'
    // Daily buckets are UTC days; in local time they would read a day early.
    ? { month: 'short', day: 'numeric', timeZone: 'UTC' }
    : { month: 'short', day: 'numeric', hour: 'numeric' });

/** Checks shown per bucket, with the share nobody passed in red; CSS bars, as the traffic sparkline. */
const Series: React.FC<{ data: Analytics }> = ({ data }) => {
  const series = data.series ?? [];
  if (series.length === 0) return null;
  const peak = Math.max(...series.map((p) => p.issued), 1);
  return (
    <div className="mt-4">
      <div className="flex h-20 items-end gap-0.5">
        {series.map((p) => {
          const unsolved = Math.max(p.issued - p.solved, 0);
          return (
            <div
              key={p.t}
              title={`${fmtBucket(p.t, data.bucket)}: ${p.issued} shown, ${p.solved} passed, ${unsolved} not passed`}
              className="flex-1 overflow-hidden rounded-sm bg-emerald-300"
              style={{ height: `${Math.max(2, (p.issued / peak) * 100)}%` }}
            >
              <div
                className="w-full bg-red-400"
                style={{ height: `${p.issued ? (unsolved / p.issued) * 100 : 0}%` }}
              />
            </div>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-gray-400">
        {fmtBucket(series[0].t, data.bucket)} — {fmtBucket(series[series.length - 1].t, data.bucket)},
        per {data.bucket}; red is the share not passed
      </p>
    </div>
  );
};

const TopList: React.FC<{ title: string; rows?: TurnstileTopRow[] }> = ({ title, rows = [] }) => {
  const peak = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="min-w-0">
      <p className="mb-2 text-xs font-medium text-gray-500">{title}</p>
      {rows.length === 0 && <p className="text-xs text-gray-400">None in this window.</p>}
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.name} className="text-sm">
            <div className="flex justify-between gap-3">
              <span className="truncate text-gray-800">{r.name}</span>
              <span className="tabular-nums text-gray-600">{fmtNum(r.count)}</span>
            </div>
            <div className="mt-0.5 h-1 rounded-full bg-gray-100">
              <div
                className="h-1 rounded-full bg-gray-400"
                style={{ width: `${(r.count / peak) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const TurnstileAnalytics: React.FC<{ days: number }> = ({ days }) => {
  const query = useTurnstileAnalyticsQuery(days * 24);
  const data = query.data;

  if (query.isLoading) return <Skeleton className="h-40 w-full" />;
  if (query.isError || !data) {
    return (
      <p className="text-sm text-red-600">
        Turnstile analytics could not be loaded. Try Refresh in a moment.
      </p>
    );
  }
  if (!data.configured) {
    return (
      <p className="text-sm text-amber-600">
        The Cloudflare analytics token is not set on ff-api-external, so there is nothing to show.
      </p>
    );
  }

  // Windows longer than a week come from the days ff-api has stored; say so
  // while that history is younger than the window being asked for.
  const windowStart = new Date(Date.now() - (days - 1) * 86_400_000).toISOString().slice(0, 10);
  const historyShort = !!data.history_since && data.history_since > windowStart;

  return (
    <div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Checks shown" value={fmtNum(data.issued)} />
        <Stat label="Passed" value={fmtNum(data.solved)} />
        <Stat label="Not passed" value={fmtNum(data.unsolved)} hint="abandoned or blocked" />
        <Stat label="Pass rate" value={fmtPct(data.solve_rate)} />
        <Stat
          label="Needed a click"
          value={fmtNum(data.interactive)}
          hint={`${fmtNum(data.non_interactive)} passed without one`}
        />
      </div>
      {historyShort && (
        <p className="mt-3 text-xs text-gray-400">
          Our stored history starts {fmtBucket(`${data.history_since}T00:00:00Z`, 'day')}, so this
          window is not full yet.
        </p>
      )}
      <Series data={data} />
      <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-3">
        <TopList title="Hostnames" rows={data.hostnames} />
        <TopList title="Browsers" rows={data.browsers} />
        <TopList title="Countries" rows={data.countries} />
      </div>
    </div>
  );
};

export default TurnstileAnalytics;
